import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, Camera, ScanLine, Gauge, Wrench, Car, FileText, Star, MapPin, ArrowRight,
  CheckCircle, Send, Route, ClipboardCheck, Award, Building2, Users, Globe, Facebook, HelpCircle, BadgeCheck,
  Phone, Mail,
} from 'lucide-react';
import BrandHero from '../components/BrandHero';
import ToolkitPromo from '../components/ToolkitPromo';
import { Seo } from '../components/academy/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scoreColor = (s) => (s >= 85 ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' : s >= 70 ? 'text-amber-400 border-amber-500/40 bg-amber-500/10' : 'text-orange-400 border-orange-500/40 bg-orange-500/10');

const SAMPLE_REPORT_IMG = 'https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/sdaxej2l_47BB2B63-4434-420A-B52E-947A20C95406.png';
const HERO_IMG = 'https://images.unsplash.com/photo-1617788138017-80ad40651399?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const GOOGLE_REVIEWS_URL = 'https://share.google/6G8NttFgPUaX5zKRF';
const REVIEWS = [
  { name: 'dexter04', initial: 'D', rating: 5, body: '10 out of 10, will recommend. I was looking to purchase a vehicle and CarLens Auto Inspections took the fear out of buying a used vehicle \u2014 very professional, very thorough, and made the whole process very easy. From now on, anytime I go to buy a car I\u2019ll definitely be using them.' },
  { name: 'Mehdi B.', initial: 'M', rating: 5, body: 'Amazing service that I cannot ever imagine buying another used car without. Saved me thousands of dollars and a potential headache.' },
  { name: 'Evelina G.', initial: 'E', rating: 5, body: 'He did an inspection on a used car I was interested in buying. He was very helpful and explained everything in detail, and was honest too! Would totally recommend him! Great customer service!' },
  { name: 'Steve R.', initial: 'S', rating: 5, body: 'My technician was very professional and provided the most detailed report I\u2019ve ever seen on the condition of the truck I was interested in buying. Answered all of my questions.' },
  { name: 'JT D.', initial: 'J', rating: 5, body: 'One of my players was looking to purchase a car and Daniel was amazing in all of his help. I would absolutely recommend them!' },
  { name: 'Trace D.', initial: 'T', rating: 5, body: 'Had them do an inspection on a vehicle I already own and they were very insightful and helpful \u2014 pointed out things I wouldn\u2019t have caught on my own.' },
];

const RECEIVE_ITEMS = [
  { Icon: ClipboardCheck, t: '200+ Point Independent Inspection' },
  { Icon: Camera, t: '30\u201350 Detailed Vehicle Photos' },
  { Icon: ScanLine, t: 'OBD-II Diagnostic Scan' },
  { Icon: Gauge, t: 'Tire Tread Measurements' },
  { Icon: Wrench, t: 'Mechanical & Safety Assessment' },
  { Icon: Car, t: 'Exterior / Interior Condition' },
  { Icon: Route, t: 'Road Test When Permitted' },
  { Icon: FileText, t: 'Upcoming Ownership Outlook' },
  { Icon: Award, t: 'Vehicle Decision Report\u2122' },
  { Icon: BadgeCheck, t: 'CarLens Confidence Score\u2122' },
  { Icon: ShieldCheck, t: 'Buy / Negotiate / Walk Away Guidance' },
];

const STEPS = [
  { n: '1', t: 'Send Us the Vehicle', d: 'Provide the listing, VIN, dealership or private-seller information, and the vehicle location.' },
  { n: '2', t: 'We Go to the Car', d: 'CarLens coordinates access and performs the inspection at the dealership or seller\u2019s location.' },
  { n: '3', t: 'We Inspect & Document', d: '200+ inspection points, OBD-II scan, mechanical condition, tires, brakes, fluids, exterior, interior, undercarriage, a road test when permitted, and 30\u201350 detailed photos.' },
  { n: '4', t: 'You Get the Truth', d: 'Receive the CarLens Vehicle Decision Report\u2122 with findings, concerns, ownership considerations, your CarLens Confidence Score\u2122, and clear decision guidance before moving forward.' },
];

const PERFECT_FOR = [
  { Icon: Globe, t: 'Out-of-State Buyers', d: 'Buying a vehicle located in Florida without being able to see it personally.' },
  { Icon: ScanLine, t: 'Online Car Buyers', d: 'Vehicles found on Autotrader, Cars.com, CarGurus, Facebook Marketplace, dealership websites, or auctions.' },
  { Icon: Building2, t: 'Dealership Purchases', d: 'We perform the inspection at the dealership so you don\u2019t have to travel just to evaluate the vehicle.' },
  { Icon: Users, t: 'Private Seller Purchases', d: 'CarLens coordinates with the seller and inspects the vehicle where it is located.' },
];

const FAQS = [
  { q: 'Can I have a used car inspected if I live in another state?', a: 'Yes. This is exactly what CarLens specializes in for remote buyers. You can be located anywhere in the United States \u2014 as long as the vehicle is within our Northeast Florida inspection service area, we go to it, inspect it, and send you a full Vehicle Decision Report\u2122 so you can decide with confidence from home.' },
  { q: 'Can CarLens inspect a vehicle at a dealership?', a: 'Yes. We regularly perform inspections at franchise and independent dealerships across Northeast Florida. Reputable dealers allow an independent pre-purchase inspection, and we handle the coordination so you never have to travel just to evaluate the car.' },
  { q: 'Can you coordinate directly with the seller or dealership?', a: 'Absolutely. Once you send us the listing and contact details, CarLens coordinates access and scheduling directly with the dealership or private seller. You don\u2019t have to manage the back-and-forth.' },
  { q: 'Do I need to be present for the inspection?', a: 'No. Most out-of-state and remote buyers are not present. We inspect the vehicle on-site and deliver your report electronically, so you can review everything from anywhere.' },
  { q: 'How will I receive my inspection report?', a: 'You receive the CarLens Vehicle Decision Report\u2122 electronically \u2014 typically the same day \u2014 including findings, 30\u201350 photos, the OBD-II diagnostic results, your CarLens Confidence Score\u2122, and clear buy / negotiate / walk-away guidance.' },
  { q: 'Can you inspect a Facebook Marketplace vehicle?', a: 'Yes. Marketplace and other online-listing inspections are among our most requested services. We coordinate with the seller and inspect the vehicle in person before you send any money or arrange transport.' },
  { q: 'Does a Carfax replace a pre-purchase inspection?', a: 'No. A vehicle-history report is a useful piece of the puzzle, but it cannot show current mechanical condition, tire and brake wear, fluid leaks, stored warning codes, cosmetic repairs, current damage, abnormal noises, or many developing concerns. A physical inspection reveals the car\u2019s condition today.' },
  { q: 'What happens if the inspection finds problems?', a: 'You get documented findings with photos and, where relevant, estimated concerns. That gives you concrete leverage to negotiate a fairer price, ask the seller to make repairs, or walk away before you commit \u2014 often saving far more than the cost of the inspection.' },
  { q: 'Can CarLens tell me whether I should buy the vehicle?', a: 'Yes. Every report ends with a clear CarLens Confidence Score\u2122 and straightforward guidance \u2014 buy, negotiate, or walk away \u2014 based on what we actually find, so you\u2019re never left guessing.' },
  { q: 'Where in Florida does CarLens perform inspections?', a: 'CarLens performs mobile inspections throughout Jacksonville and Northeast Florida, including Orange Park, Fleming Island, St. Johns, Ponte Vedra Beach, St. Augustine, Fernandina Beach / Amelia Island, Green Cove Springs, and surrounding areas. The buyer can be anywhere in the U.S.; the vehicle must be within our service area.' },
];

const PHONE_DISPLAY = '321.300.3256';
const PHONE_TEL = '+13213003256';
const EMAIL = 'CarLensAuto@gmail.com';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'Washington, D.C.',
];

const AEO_FAQS = [
  { q: 'Can CarLens inspect a car in Florida if I live in another state?', a: 'Yes. CarLens specializes in out-of-state and remote buyers. You can live in any of the 50 states \u2014 as long as the vehicle is located in Jacksonville or Northeast Florida, we go to it, inspect it in person, and send you a full Vehicle Decision Report\u2122 so you can decide with confidence from home. Call 321.300.3256 or email CarLensAuto@gmail.com to get started.' },
  { q: 'How do I get started with an out-of-state car inspection in Florida?', a: 'It takes about two minutes. Call or text CarLens at 321.300.3256, or email CarLensAuto@gmail.com, and send us the listing link, VIN, and the dealership or seller\u2019s location. We coordinate access, perform the inspection, and deliver your report \u2014 usually the same day.' },
  { q: 'How much does an out-of-state pre-purchase car inspection cost?', a: 'A CarLens pre-purchase inspection starts at $199. That\u2019s a small fraction of what one hidden mechanical problem can cost, and it gives out-of-state buyers documented leverage to negotiate or walk away before sending money or arranging transport.' },
  { q: 'How quickly will I receive my inspection report?', a: 'Most out-of-state buyers receive their CarLens Vehicle Decision Report\u2122 the same day the inspection is completed \u2014 including findings, 30\u201350 photos, the OBD-II diagnostic results, and a clear buy, negotiate, or walk-away recommendation.' },
];

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Out-of-State Pre-Purchase Car Inspection in Florida',
  serviceType: 'Pre-purchase vehicle inspection for out-of-state and remote buyers',
  description: 'Independent mobile pre-purchase vehicle inspections in Jacksonville and Northeast Florida for used-car buyers located anywhere in the United States. 200+ inspection points, OBD-II diagnostics, 30\u201350 photos, and a detailed Vehicle Decision Report.',
  provider: {
    '@type': 'AutomotiveBusiness',
    name: 'CarLens Inspections',
    url: 'https://carlensauto.com',
    telephone: PHONE_TEL,
    email: EMAIL,
    areaServed: { '@type': 'State', name: 'Florida' },
  },
  areaServed: { '@type': 'Country', name: 'United States' },
  audience: { '@type': 'Audience', audienceType: 'Out-of-state and remote used-car buyers' },
  offers: { '@type': 'Offer', priceCurrency: 'USD', price: '149', url: 'https://carlensauto.com/out-of-state-car-inspection' },
};

const HOWTO_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to inspect a used car in Florida when buying from out of state',
  description: 'A simple 4-step process for out-of-state buyers to get an independent pre-purchase inspection on a vehicle located in Jacksonville or Northeast Florida.',
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Send us the vehicle', text: 'Call or text 321.300.3256, or email CarLensAuto@gmail.com, with the listing link, VIN, and the dealership or private-seller location.' },
    { '@type': 'HowToStep', position: 2, name: 'We go to the car', text: 'CarLens coordinates access and travels to the vehicle at the dealership or seller\u2019s location in Northeast Florida.' },
    { '@type': 'HowToStep', position: 3, name: 'We inspect & document', text: 'A certified inspector performs a 200+ point inspection, OBD-II scan, road test when permitted, and takes 30\u201350 detailed photos.' },
    { '@type': 'HowToStep', position: 4, name: 'You get the truth', text: 'You receive the CarLens Vehicle Decision Report with findings, photos, your Confidence Score, and clear buy / negotiate / walk-away guidance \u2014 usually the same day.' },
  ],
};

const SPEAKABLE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Out-of-State Car Inspection in Florida',
  speakable: { '@type': 'SpeakableSpecification', cssSelector: ['#aeo-quick-answer', 'h1'] },
  url: 'https://carlensauto.com/out-of-state-car-inspection',
};

const BREADCRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'Out-of-State Car Inspection', path: '/out-of-state-car-inspection' },
];

const OutOfStateInspection = () => {
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    axios.get(`${API}/recent-inspections`)
      .then((res) => setRecent((res.data || []).slice(0, 3)))
      .catch(() => setRecent([]));
  }, []);

  const heroImg = HERO_IMG;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }} data-testid="out-of-state-page">
      <Seo
        siteName="CarLens"
        title="Out-of-State Car Inspection in Florida | All 50 States"
        description="Buying a used car in Jacksonville or Northeast Florida from another state? CarLens is your independent eyes on the ground for out-of-state buyers in all 50 states — 200+ point pre-purchase inspection, photos, OBD-II diagnostics, and a Vehicle Decision Report before you buy. Call 321.300.3256."
        path="/out-of-state-car-inspection"
        faq={[...FAQS, ...AEO_FAQS]}
        breadcrumbs={BREADCRUMBS}
        extraSchemas={[SERVICE_SCHEMA, HOWTO_SCHEMA, SPEAKABLE_SCHEMA]}
      />

      <BrandHero />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-10 pb-16 md:pt-14" data-testid="oos-hero">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.06]" style={{ fontFamily: 'Space Grotesk' }}>
              Buying a Car in Florida From Out of State?
            </h1>
            <p className="mt-6 text-lg md:text-xl text-zinc-300 leading-relaxed">
              You don&rsquo;t have to fly to Florida just to find out if the car is worth buying.
            </p>
            <p className="mt-4 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl">
              CarLens acts as your independent eyes on the ground. We go directly to the dealership or private seller, inspect the vehicle, document what we find, and give you the information you need to make a confident decision from anywhere in the country.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Button
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-orange-500/20"
                onClick={() => navigate('/book')}
                data-testid="oos-hero-book-btn"
              >
                Book an Inspection
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-white/5 px-8 py-6 text-lg rounded-lg"
                onClick={() => navigate('/what-you-receive')}
                data-testid="oos-hero-sample-btn"
              >
                View Sample Report
              </Button>
            </div>
            <p className="mt-6 text-sm text-zinc-400 tracking-wide">
              Independent • Mobile • 200+ Inspection Points • 30&ndash;50 Photos • OBD-II Diagnostics • Vehicle Decision Report&trade;
            </p>

            {/* AEO quick-answer box (speakable target) */}
            <div id="aeo-quick-answer" className="mt-6 rounded-2xl border border-orange-500/30 bg-orange-500/5 p-5" data-testid="oos-aeo-answer">
              <p className="text-zinc-200 text-sm md:text-base leading-relaxed">
                <strong className="text-white">Yes &mdash; CarLens inspects vehicles for out-of-state buyers in all 50 states.</strong> If the car you&rsquo;re buying is located in Jacksonville or Northeast Florida, we go to it in person and send you a full pre-purchase inspection report so you can decide from anywhere in the country.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 transition-colors" data-testid="oos-call-btn">
                  <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
                </a>
                <a href={`mailto:${EMAIL}`} className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-700 text-zinc-200 hover:bg-white/5 font-semibold px-5 py-2.5 transition-colors" data-testid="oos-email-btn">
                  <Mail className="w-4 h-4" /> {EMAIL}
                </a>
              </div>
            </div>
          </div>
          <div className="relative mx-auto w-full max-w-lg">
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/25 to-zinc-600/20 rounded-3xl blur-3xl" />
            <img
              src={heroImg}
              alt="Tesla at a dealership showroom, inspected by CarLens for an out-of-state buyer"
              className="relative rounded-2xl shadow-2xl border-2 border-zinc-700/60 w-full h-auto object-cover aspect-[4/3]"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* YOUR EYES ON THE GROUND */}
      <section className="bg-gradient-to-b from-zinc-50 to-white py-24" data-testid="oos-process">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Space Grotesk' }}>
              Buying From Hundreds of Miles Away Shouldn&rsquo;t Mean Buying Blind.
            </h2>
            <p className="mt-5 text-zinc-600 text-lg leading-relaxed">
              Photos, listings, salesperson descriptions, history reports, and video calls simply cannot replace an independent physical inspection. CarLens physically goes to the vehicle and evaluates it before you commit to buying it.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white rounded-2xl border border-zinc-200 p-7 shadow-xl" data-testid={`oos-step-${s.n}`}>
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-2xl font-bold mb-5 shadow-lg shadow-orange-500/20" style={{ fontFamily: 'Space Grotesk' }}>
                  {s.n}
                </div>
                <h3 className="text-lg font-bold text-zinc-900 mb-2">{s.t}</h3>
                <p className="text-zinc-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU RECEIVE */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="oos-receive">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>What You Receive</h2>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">Everything you need to evaluate the vehicle in one clear, independent report.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {RECEIVE_ITEMS.map((it, i) => (
            <div key={i} className="flex items-center gap-4 rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-5 hover:border-orange-500/50 transition-colors" data-testid={`oos-receive-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
                <it.Icon className="w-5 h-5 text-orange-400" />
              </div>
              <span className="text-white font-medium leading-snug">{it.t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* REAL INSPECTIONS */}
      {recent.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16" data-testid="oos-recent">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>Real Inspections. Real Findings.</h2>
            <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">Actual vehicles our inspectors recently evaluated \u2014 each with a CarLens Confidence Score\u2122. Customer details are always kept private.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recent.map((it) => (
              <Link key={it.id} to="/recent-inspections" className="group rounded-2xl border border-zinc-800 bg-[#151517] overflow-hidden hover:border-orange-500/50 transition-colors" data-testid={`oos-recent-card-${it.id}`}>
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={(it.photos || [])[0]} alt={`${it.vehicle} inspected by CarLens in Florida`} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <span className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full border ${scoreColor(it.score)}`}>{it.score} Score</span>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-white leading-tight group-hover:text-orange-300 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>{it.vehicle}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">{it.when}</p>
                  <p className="mt-3 text-sm text-zinc-400 leading-6 line-clamp-3">{it.summary}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/recent-inspections" className="inline-flex items-center gap-2 rounded-full border border-orange-500/50 text-orange-300 hover:bg-orange-500/10 px-7 py-3 font-semibold transition-colors" data-testid="oos-see-recent-btn">
              See Recent Inspections <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* SEE WHAT YOU'LL RECEIVE (sample report) */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="oos-sample">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative mx-auto w-full max-w-md">
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/25 to-zinc-600/20 rounded-3xl blur-3xl" />
            <img
              src={SAMPLE_REPORT_IMG}
              alt="Sample CarLens Vehicle Decision Report executive summary"
              className="relative rounded-2xl shadow-2xl border-2 border-zinc-700/60 w-full h-auto cursor-pointer hover:scale-[1.02] transition-transform duration-300"
              onClick={() => navigate('/what-you-receive')}
              loading="lazy"
            />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>Know More Than What&rsquo;s in the Listing.</h2>
            <p className="text-zinc-400 text-lg leading-relaxed mb-8">
              Your CarLens Vehicle Decision Report&trade; turns a stranger&rsquo;s listing into a documented, independent assessment \u2014 findings, photos, diagnostics, ownership outlook, and a clear recommendation you can act on from anywhere.
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-orange-500/20"
              onClick={() => navigate('/what-you-receive')}
              data-testid="oos-sample-btn"
            >
              View Sample Vehicle Decision Report
            </Button>
          </div>
        </div>
      </section>

      {/* PERFECT FOR */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="oos-perfect-for">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>Perfect For</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PERFECT_FOR.map((c) => (
            <div key={c.t} className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-7 hover:border-orange-500/50 transition-colors" data-testid={`oos-perfect-${c.t.split(' ')[0].toLowerCase()}`}>
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                <c.Icon className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{c.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY A HISTORY REPORT ISN'T ENOUGH */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="oos-history">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>Why a History Report Isn&rsquo;t Enough</h2>
        <div className="space-y-5 text-zinc-300 text-base md:text-lg leading-relaxed">
          <p>
            A vehicle-history report is a genuinely useful tool \u2014 it can surface reported accidents, title branding, and ownership history. But it is only one piece of a smart buying decision, and it has real limits.
          </p>
          <p>
            A history report cannot show a vehicle&rsquo;s <strong className="text-white">current</strong> mechanical condition, tire and brake wear, active fluid leaks, stored or pending diagnostic codes, quality of any cosmetic or accident repairs, present damage, abnormal noises, or the many developing concerns that only reveal themselves during a hands-on inspection and road test.
          </p>
          <p>
            That&rsquo;s why the smartest remote buyers pair a history report with an independent <Link to="/services/pre-purchase-inspection" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">pre-purchase inspection</Link>. Together, they give you the full picture: the car&rsquo;s past and its condition today. Want the complete breakdown? Read our <Link to="/academy/complete-guide-to-pre-purchase-inspections" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">complete guide to pre-purchase inspections</Link>.
          </p>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <section className="py-24 bg-gradient-to-b from-white to-zinc-100" data-testid="oos-reviews">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>What Our Customers Say</h2>
            <div className="inline-flex items-center gap-3 rounded-full border border-zinc-200 bg-white px-5 py-2.5 shadow-sm">
              <span className="text-2xl font-extrabold text-zinc-900" style={{ fontFamily: 'Space Grotesk' }}>5.0</span>
              <span className="flex items-center gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />)}</span>
              <span className="text-zinc-500 text-sm"><span className="font-semibold text-zinc-700">5-star reviews</span></span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {REVIEWS.map((r) => (
              <div key={r.name} className="bg-white rounded-2xl p-6 shadow-xl flex flex-col" data-testid={`oos-review-${r.initial}`}>
                <div className="flex items-center gap-1 mb-4">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />)}</div>
                <p className="text-zinc-700 mb-6 leading-relaxed flex-1">"{r.body}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg shrink-0" style={{ fontFamily: 'Space Grotesk' }}>{r.initial}</div>
                  <div>
                    <div className="font-semibold text-zinc-900">{r.name}</div>
                    <div className="flex items-center gap-1.5 text-sm text-zinc-500">
                      <img src="https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_18dp.png" alt="Google" className="w-4 h-4" loading="lazy" />
                      Google review
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-zinc-900 text-white px-7 py-3.5 font-semibold hover:bg-zinc-800 transition-colors" data-testid="oos-google-reviews-btn">
              <img src="https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_18dp.png" alt="" className="w-5 h-5 bg-white rounded-full p-0.5" />
              Read all reviews on Google
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="oos-service-area">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>Buying From Somewhere Else. Inspecting Here in Florida.</h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
            CarLens provides mobile pre-purchase inspections throughout Jacksonville and Northeast Florida. Choose the area where the vehicle is located to learn more.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {[
            { slug: 'jacksonville', name: 'Jacksonville' },
            { slug: 'orange-park', name: 'Orange Park' },
            { slug: 'fleming-island', name: 'Fleming Island' },
            { slug: 'ponte-vedra-beach', name: 'Ponte Vedra Beach' },
            { slug: 'st-augustine', name: 'St. Augustine' },
            { slug: 'fernandina-beach', name: 'Fernandina Beach' },
            { slug: 'amelia-island', name: 'Amelia Island' },
            { slug: 'green-cove-springs', name: 'Green Cove Springs' },
          ].map((loc) => (
            <Link key={loc.slug} to={`/locations/${loc.slug}`} className="group flex items-center gap-2 rounded-xl border border-zinc-800 bg-[#161618] px-4 py-4 hover:border-orange-500/50 hover:bg-[#1c1c1f] transition-all" data-testid={`oos-area-${loc.slug}`}>
              <MapPin className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span className="text-sm md:text-base font-medium text-zinc-200 group-hover:text-white">{loc.name}</span>
            </Link>
          ))}
        </div>
        <div className="mt-8 max-w-3xl mx-auto rounded-2xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 to-orange-600/10 p-6 text-center">
          <p className="text-zinc-200 leading-relaxed">
            <strong className="text-white">The buyer can be located anywhere in the United States.</strong> The vehicle must be located within the CarLens physical inspection service area in Northeast Florida.
          </p>
        </div>
        <div className="text-center mt-8">
          <Link to="/locations" className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold" data-testid="oos-all-locations-link">
            View all service areas <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="oos-faq">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <HelpCircle className="w-6 h-6 text-orange-500" />
            <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Out-of-State Buyer Questions</h2>
        </div>
        <div className="space-y-4">
          {[...FAQS, ...AEO_FAQS].map((f, i) => (
            <details key={i} className="group rounded-2xl border border-zinc-800 bg-[#161618] px-6 py-5 hover:border-orange-500/40 transition-colors" data-testid={`oos-faq-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none text-white font-semibold text-base md:text-lg">
                <span>{f.q}</span>
                <ArrowRight className="w-5 h-5 text-orange-400 flex-shrink-0 ml-4 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-4 text-zinc-300 text-base leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/services/facebook-marketplace-inspection" className="text-orange-400 hover:text-orange-300 font-medium mr-6">Facebook Marketplace Inspection</Link>
          <Link to="/services/dealer-lot-inspection" className="text-orange-400 hover:text-orange-300 font-medium mr-6">Dealer Lot Inspection</Link>
          <Link to="/services/private-seller-inspection" className="text-orange-400 hover:text-orange-300 font-medium">Private Seller Inspection</Link>
        </div>
      </section>

      {/* ALL 50 STATES */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="oos-all-states">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <Globe className="w-6 h-6 text-orange-500" />
            <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">All 50 States</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Buying From Any State. We Inspect It Here in Florida.
          </h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
            Wherever you live, CarLens is your independent eyes on the ground for a <strong className="text-zinc-200">used car inspection in Jacksonville and Northeast Florida</strong>. We help out-of-state buyers from coast to coast &mdash; the buyer can be anywhere in the U.S., and the vehicle just needs to be within our Florida service area.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5" data-testid="oos-states-grid">
          {US_STATES.map((s) => (
            <div key={s} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-[#161618] px-3 py-2.5 hover:border-orange-500/40 transition-colors" data-testid={`oos-state-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <MapPin className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
              <span className="text-sm text-zinc-300 truncate">{s}</span>
            </div>
          ))}
        </div>
        <p className="text-center text-zinc-500 text-sm mt-8 max-w-3xl mx-auto">
          Popular with buyers arranging an <span className="text-zinc-300">out-of-state car inspection in Florida</span> from Georgia, New York, New Jersey, Texas, California, Ohio, Pennsylvania, Illinois, North Carolina, and every other state &mdash; before shipping, financing, or flying in.
        </p>
      </section>

      {/* HOW TO GET STARTED (AEO + contact) */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="oos-get-started">
        <div className="rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-8 md:p-12">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>How to Get Started From Out of State</h2>
            <p className="text-zinc-400 text-base md:text-lg">Two minutes to start. Here&rsquo;s exactly how the CarLens out-of-state inspection works.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              { n: '1', t: 'Send Us the Vehicle', d: 'Call or text 321.300.3256, or email us, with the listing link, VIN, and the dealership or seller\u2019s location.' },
              { n: '2', t: 'We Go to the Car', d: 'CarLens coordinates access and travels to the vehicle anywhere in Northeast Florida.' },
              { n: '3', t: 'We Inspect & Document', d: '200+ points, OBD-II scan, road test when permitted, and 30\u201350 detailed photos.' },
              { n: '4', t: 'You Get the Truth', d: 'Receive your Vehicle Decision Report\u2122 with a clear buy / negotiate / walk-away recommendation \u2014 usually same day.' },
            ].map((s) => (
              <div key={s.n} className="text-center" data-testid={`oos-start-step-${s.n}`}>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-xl font-bold mx-auto mb-4 shadow-lg shadow-orange-500/20" style={{ fontFamily: 'Space Grotesk' }}>{s.n}</div>
                <h3 className="text-base font-bold text-white mb-1.5">{s.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-8 py-4 text-lg transition-colors shadow-lg shadow-orange-500/20" data-testid="oos-start-call-btn">
              <Phone className="w-5 h-5" /> Call or Text {PHONE_DISPLAY}
            </a>
            <a href={`mailto:${EMAIL}`} className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-700 text-zinc-200 hover:bg-white/5 font-semibold px-8 py-4 text-lg transition-colors" data-testid="oos-start-email-btn">
              <Mail className="w-5 h-5" /> {EMAIL}
            </a>
          </div>
        </div>
      </section>


      {/* Toolkit promotion */}
      <div className="max-w-7xl mx-auto px-6">
        <ToolkitPromo variant="compact" />
      </div>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="oos-final-cta">
        <div className="bg-gradient-to-r from-orange-600/20 to-orange-500/20 rounded-3xl border border-orange-500/30 p-10 md:p-14 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
            Don&rsquo;t Fly to Florida Just to Find Out It&rsquo;s the Wrong Car.
          </h2>
          <p className="text-zinc-300 text-lg mb-8 max-w-2xl mx-auto">
            Let CarLens inspect it before you make the trip, send the money, or sign the paperwork.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg"
              onClick={() => navigate('/book')}
              data-testid="oos-final-book-btn"
            >
              Book Your Out-of-State Buyer Inspection
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10 px-8 py-6 text-lg rounded-lg"
              onClick={() => navigate('/firstlook')}
              data-testid="oos-final-firstlook-btn"
            >
              CarLens FirstLook
            </Button>
          </div>
          <p className="text-zinc-400 text-sm mt-6">Already found the vehicle? Send us the listing and let&rsquo;s take a closer look.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-8">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-white font-semibold tracking-wide" style={{ fontFamily: 'Space Grotesk' }}>CarLens Automotive Intelligence</p>
              <p className="text-zinc-500 text-sm">© 2025 CarLens Automotive Intelligence. All rights reserved.</p>
            </div>
            <a href="https://www.facebook.com/profile.php?id=61583275422342" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-400 hover:text-zinc-600 transition-colors">
              <Facebook className="w-5 h-5" />
              <span>Follow us on Facebook</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OutOfStateInspection;
